import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  AgrarischeActiviteiten,
  BokkzVpbDataService,
  CorporateTaxEntity,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup} from "@angular/forms";
import {debounceTime, filter, Observable, shareReplay, take, tap} from "rxjs";
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-agrarisch',
  templateUrl: './spec-agrarisch.component.html',
  styleUrls: ['./spec-agrarisch.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class SpecAgrarischComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  public agrarischFg = new FormGroup({
    naamOnderneming: new FormControl<string | null>(null),
    aardActiviteiten: new FormControl<string | null>(null),
    afwijkingVanLandbouwnormen: new FormControl<boolean | null>(false),
    specificatieAfwijkingLandbouwnormen: new FormControl<unknown>(null),
    landbouwGrondenInBelgieOfDuitsland: new FormControl<boolean | null>(false),
    vervreemdingVanGrond: new FormControl<boolean | null>(false),
    vervreemdingOnroerendeZaken: new FormControl<boolean | null>(false),
    vervreemdingOnroerendeZakenAfgeboekteReserve: new FormControl<boolean | null>(false),
    vervreemdingAgrarischeGrondAanAgrarier: new FormControl<boolean | null>(false),
    opstallenGeslooptInVerbandMetVervreemding: new FormControl<boolean | null>(false),
    productierechtenFiscaalBegin: new FormControl<number | null>(null),
    productierechtenFiscaalEnd: new FormControl<number | null>(null),
    productierechtenFiscaalAanschaffingskosten: new FormControl<number | null>(null),
    dierenFiscaalBegin: new FormControl<number | null>(null),
    dierenFiscaalEnd: new FormControl<number | null>(null),
    dierenFiscaalAanschaffingskosten: new FormControl<number | null>(null),
    dierenFiscaalRestwaarde: new FormControl<number | null>(null),
    plantEnPootgoedFiscaalBegin: new FormControl<number | null>(null),
    plantEnPootgoedFiscaalEnd: new FormControl<number | null>(null),
    plantEnPootgoedFiscaalAanschaffingskosten: new FormControl<number | null>(null),
    plantEnPootgoedFiscaalRestwaarde: new FormControl<number | null>(null),
    overigeActivaLandbouwBegin: new FormControl<number | null>(null),
    overigeActivaLandbouwEnd: new FormControl<number | null>(null),
    overigeActivaLandbouwAanschaffingskosten: new FormControl<number | null>(null),
    overigeActivaLandbouwRestwaarde: new FormControl<number | null>(null),
    voorraadDierenFiscaalBegin: new FormControl<number | null>(null),
    voorraadDierenFiscaalEnd: new FormControl<number | null>(null),
    voorraadBoelbollenFiscaalBegin: new FormControl<number | null>(null),
    voorraadBoelbollenFiscaalEnd: new FormControl<number | null>(null),
    voorraadChampignonsFiscaalBegin: new FormControl<number | null>(null),
    voorraadChampignonsFiscaalEnd: new FormControl<number | null>(null),
    voorraadVeldinventarisFiscaalBegin: new FormControl<number | null>(null),
    voorraadVeldinventarisFiscaalEnd: new FormControl<number | null>(null),
    voorraadOverigeLandbouwFiscaalBegin: new FormControl<number | null>(null),
    voorraadOverigeLandbouwFiscaalEnd: new FormControl<number | null>(null),
    subsidieNatuurbeheer: new FormControl<number | null>(null),
    werkVoorDerden: new FormControl<number | null>(null),
    voergeldFiscaal: new FormControl<number | null>(null),
    afschrijvingProductierechten: new FormControl<number | null>(null),
  });

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (!data.aangifte) {
          return;
        }

      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.agrarischFg.patchValue(data.aangifte?.specificatieAgrarischeActiviteiten?.at(0) || {}, {emitEvent: false});
    })
    this.agrarischFg.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    this.agrarischFg.updateValueAndValidity({emitEvent: false});
    const specificatieAgrarischeActiviteiten = [
      this.agrarischFg.getRawValue() as unknown as AgrarischeActiviteiten
    ];

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          specificatieAgrarischeActiviteiten,
        } as VpbAangifte,
      };
    });
  }
}
